import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, Mail, Phone, ArrowLeft, RotateCcw, Building2, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";

interface Subcontractor {
  _id: Id<"subcontractors">;
  name: string;
  logo?: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  services: string[];
  status: "active" | "inactive" | "pending";
  archived?: boolean;
}

export default function ArchivedSubcontractorList() {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // Fetch archived subcontractors from Convex
  const archivedSubcontractors = useQuery(api.subcontractors.getArchivedSubcontractors) ?? [];
  const unarchiveSubcontractor = useMutation(api.subcontractors.unarchive);

  const filteredSubcontractors = archivedSubcontractors.filter(
    (subcontractor: any) =>
      subcontractor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      subcontractor.contactName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      subcontractor.services.some((service: any) =>
        service.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  const handleUnarchive = async (
    subcontractorId: Id<"subcontractors">,
    subcontractorName: string
  ) => {
    try {
      await unarchiveSubcontractor({ id: subcontractorId });
      toast.success(`${subcontractorName} has been unarchived successfully`);
    } catch (error) {
      console.error("Failed to unarchive subcontractor:", error);
      toast.error("Failed to unarchive subcontractor");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/subcontractors")}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Archived Subcontractors</h1>
            <p className="mt-1 text-gray-500">
              View and manage archived subcontractor relationships
            </p>
          </div>
        </div>
      </div>

      <Card className="p-6">
        <div className="mb-6 flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              placeholder="Search archived subcontractors..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button variant="outline" className="gap-2">
            <Filter className="h-4 w-4" /> Filter
          </Button>
        </div>

        {filteredSubcontractors.length === 0 ? (
          <div className="py-12 text-center">
            <div className="mb-4 text-gray-400">
              <Building2 className="mx-auto h-12 w-12" />
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              No archived subcontractors found
            </h3>
            <p className="text-gray-500">
              {searchQuery
                ? "Try adjusting your search criteria"
                : "You haven't archived any subcontractors yet"}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Subcontractor</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Services</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubcontractors.map((subcontractor: any) => (
                <TableRow key={subcontractor._id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarImage
                          src={
                            subcontractor.logo ||
                            `https://api.dicebear.com/7.x/avataaars/svg?seed=${subcontractor.name}`
                          }
                          alt={subcontractor.name}
                        />
                        <AvatarFallback>
                          {subcontractor.name
                            .split(" ")
                            .map((n: any) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{subcontractor.name}</div>
                        <div className="text-sm text-gray-500">{subcontractor.contactName}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Mail className="h-3 w-3" />
                        {subcontractor.email}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Phone className="h-3 w-3" />
                        {subcontractor.phone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <MapPin className="h-3 w-3" />
                      <span>
                        {subcontractor.city}, {subcontractor.state}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {subcontractor.services.slice(0, 2).map((service: any, index: any) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {service}
                        </Badge>
                      ))}
                      {subcontractor.services.length > 2 && (
                        <Badge variant="secondary" className="text-xs">
                          +{subcontractor.services.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={subcontractor.status === "active" ? "default" : "secondary"}
                      className={
                        subcontractor.status === "active"
                          ? "bg-green-100 text-green-800"
                          : subcontractor.status === "inactive"
                            ? "bg-gray-100 text-gray-800"
                            : "bg-yellow-100 text-yellow-800"
                      }
                    >
                      {subcontractor.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => handleUnarchive(subcontractor._id, subcontractor.name)}
                    >
                      <RotateCcw className="h-3 w-3" />
                      Unarchive
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
